// Per-line "Indpakning i gavepose" toggle on the cart drawer and cart page.
//
// TWO MODES, decided per line by whether Zepto's cart transform manages it (an "armed"
// line carries `_pplr_cart_variant`):
//
//   UNARMED line — checking the box adds ONE nested `indpakning-i-gavepose` child line
//   under the product line (`parent_line_key`), quantity = the parent's quantity;
//   unchecking zeroes it. The nesting IS the whole association: it survives every
//   parent re-key (Shopify migrates `parent_relationship` itself), it removes the bag
//   with the parent natively, and the order confirmation email renders the child
//   indented under its product (order-confirmation-v12 renders any nested child, it is
//   not Gravering-specific).
//
//   ARMED line — Shopify REJECTS a nested child under any transform-managed line
//   (422 "Sampak-produkter og tilføjelser kan ikke blandes", verified 2026-08-25), so
//   these keep the flat design: the toggle stamps/removes the VISIBLE `Gavepose: Ja`
//   property (mirrored into Zepto's `_pplr_attribute_data` snapshot, which otherwise
//   overwrites the property set at order creation), and the reconciler in
//   byRavn-gravering.js derives the ONE flat propertyless bag line from those flags.
//   The flat design is permanent: BOX is never zeroed (its whole price is Zepto's
//   per-character fee), so its lines always arm.
//
// This file owns only the INTENT side of a toggle — child existence (unarmed) or the
// flag (armed); that is user intent, underivable from the cart. Every size invariant
// (bag child quantity follows parent quantity, flat bag quantity = flagged units)
// belongs to the reconciler, which runs after each armed toggle and behind every cart
// mutation.
//
// THE NONCE. Two same-variant parents differing only in their engraving get different
// parent keys but would get IDENTICAL bag-child keys (the child key hashes the child's
// variant and properties, not the parent line) — /cart/change on that shared key hits
// BOTH children. Every bag child therefore carries an opaque `_byRavn_gavepose_ref`
// nonce, exactly like the fee children's `_byRavn_gravering_ref`.
//
// The change listener runs in the capture phase and stops propagation so the event
// never reaches Dawn's <cart-items>/<cart-drawer-items> quantity handler, which would
// otherwise mis-read this checkbox as a quantity input.
(function () {
  const REF_PROP = '_byRavn_gavepose_ref';
  // Stored constant and Danish, like every other property in this theme (the Zepto
  // field names are Danish too) — cart-line-properties.liquid translates both the
  // label and this one value for display. KEEP IN SYNC with the `Gavepose` entries
  // there and with FLAG_PROP in byRavn-gravering.js.
  const FLAG_PROP = 'Gavepose';
  const FLAG_VALUE = 'Ja';

  function surfaceFor(element) {
    return element.closest('cart-items, cart-drawer-items');
  }

  // Section-rendering helpers live in byRavn-cart-sections.js — shared with
  // byRavn-cart-preview-strip.js, which needs the same painting semantics.
  const { surfaces: cartSurfaces, paintAllSurfaces, fetchCartSections, setBusy, patchZeptoSnapshot } = window.byRavnCart;
  function fetchCart() {
    return fetch(`${window.routes.cart_url}.js`).then((response) => response.json());
  }

  // Shopify's AJAX cart answers errors with a JSON body carrying `status` (422 etc.);
  // a successful cart or line item never has that field.
  function postCart(url, payload) {
    return fetch(url, { ...window.fetchConfig(), body: JSON.stringify(payload) })
      .then((response) => response.json())
      .then((body) => {
        if (body && (body.status || body.errors)) throw new Error(body.description || body.message || 'cart error');
        return body;
      });
  }

  function nonce() {
    const bytes = new Uint32Array(2);
    crypto.getRandomValues(bytes);
    return 'b' + bytes[0].toString(36) + bytes[1].toString(36);
  }

  // The bag variant id comes from the shared config (byravn-gravering-config.liquid)
  // rather than a data attribute, so the toggle and the reconciler can never disagree
  // about which product is the bag. It is also the ONLY way a cart line is recognised
  // as the bag: `item.handle` in /cart.js is the LOCALIZED handle (the Swedish
  // storefront reports `inslagning-i-presentpase`), so matching the canonical handle
  // silently missed every bag line on a translated locale.
  function bagVariantId() {
    const config = window.byRavnGravering;
    return (config && config.gavepose && config.gavepose.variantId) || 0;
  }

  // `_pplr_cart_variant` IS Zepto's cart-transform marker — same contract as
  // byRavn-gravering.js (Zepto's hide-in-checkout rewrite prefixes one more `_`, so
  // match the de-underscored name).
  function isArmed(item) {
    return Object.keys(item.properties || {}).some((name) => name.replace(/^_+/, '') === 'pplr_cart_variant');
  }

  function isFlagged(item) {
    return !!(item.properties && item.properties[FLAG_PROP]);
  }

  function bagChildrenOf(cart, parentKey) {
    return (cart.items || []).filter(
      (item) =>
        item.id === bagVariantId() &&
        item.parent_relationship &&
        item.parent_relationship.parent_key === parentKey
    );
  }

  function isWrapped(cart, item) {
    return isFlagged(item) || bagChildrenOf(cart, item.key).length > 0;
  }

  // The reconciler owns the flat bag's size (it derives it from the visible flags), so
  // an armed toggle's flag write is only half the job — run a pass before repainting.
  // The module boundary is the shared config object; if byRavn-gravering.js has not
  // defined reconcile yet (both files are deferred, this one first), the page-load /
  // cartUpdate reconcile catches up instead.
  function runReconcile() {
    const config = window.byRavnGravering;
    return config && typeof config.reconcile === 'function'
      ? config.reconcile('gavepose-toggle')
      : Promise.resolve();
  }

  // Zepto rewrites an added line's whole property set from its own snapshot ~1.5s after
  // every add, re-keying the line — and nothing repaints the drawer afterwards, so the
  // first toggle after an engraved add can hold a dead key. Hold toggles until the
  // rewrite lands (broadcast by byRavn-cart-drawer-open.js, which owns the XHR patch);
  // the timeout covers adds where Zepto skips the rewrite.
  let zeptoSettled = null;
  document.addEventListener('byRavn:zepto-add', () => {
    zeptoSettled = new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer);
        document.removeEventListener('byRavn:zepto-change', done);
        zeptoSettled = null;
        resolve();
      };
      const timer = setTimeout(done, 4000);
      document.addEventListener('byRavn:zepto-change', done);
    });
  });

  // The rendered data-line-key can be dead by the time it is clicked (Zepto's re-key).
  // The stale key still names the variant (`<variant id>:<property hash>`), so recover
  // the line by content — but never guess: byRavn routinely carries several
  // same-variant lines that differ only in their engraving, and only a single
  // surviving candidate is safe to act on. The disambiguator is the wrapped state the
  // checkbox believed it was toggling FROM.
  function resolveStaleLineKey(cart, lineKey, wantWrapped) {
    const variantId = parseInt(lineKey, 10);
    const candidates = (cart.items || []).filter(
      (item) =>
        item.id === variantId &&
        item.id !== bagVariantId() &&
        !(item.parent_relationship && item.parent_relationship.parent_key) &&
        isWrapped(cart, item) === wantWrapped
    );
    return candidates.length === 1 ? candidates[0] : null;
  }

  function samePropertySet(a, b) {
    const left = a || {};
    const right = b || {};
    const keys = Object.keys(left);
    if (keys.length !== Object.keys(right).length) return false;
    return keys.every((key) => left[key] === right[key]);
  }

  // A property change re-hashes the line key, so after an armed toggle the line has to
  // be found again for the focus restore. Exact property-set match first; if that
  // misses (Shopify may merge the result into an identical line), fall back to the
  // only same-variant line whose flag state matches what was just written.
  function resolveLine(cart, variantId, properties) {
    const items = cart.items || [];
    const exact = items.find((item) => item.id === variantId && samePropertySet(item.properties, properties));
    if (exact) return exact;
    const want = !!properties[FLAG_PROP];
    const candidates = items.filter(
      (item) => item.id === variantId && item.id !== bagVariantId() && isFlagged(item) === want
    );
    return candidates.length === 1 ? candidates[0] : null;
  }

  async function addGavepose(checkbox) {
    const variantId = bagVariantId();
    if (!variantId) throw new Error('gavepose product unavailable');
    const lineKey = checkbox.dataset.lineKey;
    const live = await fetchCart();
    const parent =
      (live.items || []).find((item) => item.key === lineKey) || resolveStaleLineKey(live, lineKey, false);
    if (!parent) throw new Error('stale line key');
    let focusKey = parent.key;
    if (isWrapped(live, parent)) {
      // Already wrapped (state desync between the two surfaces): the repaint below
      // renders the true state.
    } else if (isArmed(parent)) {
      // FLAT PATH — nesting under a transform-managed line 422s. /cart/change
      // REPLACES the property set, so every existing property is resent verbatim.
      const properties = { ...parent.properties, [FLAG_PROP]: FLAG_VALUE };
      // Mirror into Zepto's snapshot too — a flag written only on the line vanishes
      // from the order when Zepto restores the snapshot at order creation.
      patchZeptoSnapshot(properties, (snapshot) => {
        snapshot[FLAG_PROP] = FLAG_VALUE;
      });
      const stamped = await postCart(window.routes.cart_change_url, {
        id: parent.key,
        quantity: parent.quantity,
        properties,
      });
      const wrapped = resolveLine(stamped, parent.id, properties);
      if (wrapped) focusKey = wrapped.key;
      await runReconcile();
    } else {
      await postCart(window.routes.cart_add_url, {
        items: [
          {
            id: variantId,
            quantity: parent.quantity,
            parent_line_key: parent.key,
            properties: { [REF_PROP]: nonce() },
          },
        ],
      });
    }
    return { state: await fetchCartSections(), lineKey: focusKey };
  }

  async function removeGavepose(checkbox) {
    const lineKey = checkbox.dataset.lineKey;
    const live = await fetchCart();
    const parent =
      (live.items || []).find((item) => item.key === lineKey) || resolveStaleLineKey(live, lineKey, true);
    let focusKey = parent ? parent.key : lineKey;
    if (parent) {
      // Plural on purpose: a duplicate child (two half-finished passes racing) must not
      // survive an explicit "no bag".
      for (const child of bagChildrenOf(live, parent.key)) {
        await postCart(window.routes.cart_change_url, { id: child.key, quantity: 0 });
      }
      if (isFlagged(parent)) {
        // Flat design: drop the visible flag; the reconcile below shrinks the flat bag
        // to match. Dropping it may merge this line back into an identical unwrapped
        // one — the correct outcome, and resolveLine follows it to the surviving key.
        const properties = { ...parent.properties };
        delete properties[FLAG_PROP];
        // Shopify cannot clear a line's LAST property (an empty `properties` object is
        // ignored), so a line whose only property is the flag gets a blank flag instead:
        // every reader here and in the Liquid treats a blank flag as unwrapped.
        if (!Object.keys(properties).length) properties[FLAG_PROP] = '';
        // Mirror into Zepto's snapshot too — a flag removed only from the line comes
        // back on the order when Zepto restores the snapshot at order creation.
        patchZeptoSnapshot(properties, (snapshot) => {
          delete snapshot[FLAG_PROP];
        });
        const cleared = await postCart(window.routes.cart_change_url, {
          id: parent.key,
          quantity: parent.quantity,
          properties,
        });
        const unwrapped = resolveLine(cleared, parent.id, properties);
        if (unwrapped) focusKey = unwrapped.key;
        await runReconcile();
      }
    }
    return { state: await fetchCartSections(), lineKey: focusKey };
  }

  async function onToggle(checkbox) {
    const surface = surfaceFor(checkbox);
    if (!surface) return;
    const row = checkbox.closest('.cart-gavepose');
    const adding = checkbox.checked;
    checkbox.disabled = true;
    if (row) row.classList.add('cart-gavepose--busy');
    setBusy(true);

    try {
      if (zeptoSettled) await zeptoSettled;
      const { state, lineKey } = adding ? await addGavepose(checkbox) : await removeGavepose(checkbox);
      if (state && state.sections) {
        paintAllSurfaces(state, surface, 'byRavn-gavepose');
        // Scope to the toggled surface's own container — on /cart the closed drawer
        // holds a hidden row with the same data-line-key earlier in document order.
        const surfaceRoot = document.getElementById(surface.getSectionsToRender()[0].id);
        const fresh = surfaceRoot && surfaceRoot.querySelector(`.cart-gavepose__checkbox[data-line-key="${lineKey}"]`);
        if (fresh) fresh.focus({ preventScroll: true });
      } else {
        cartSurfaces().forEach((s) => s.onCartUpdate());
      }
    } catch (error) {
      checkbox.checked = !adding;
      checkbox.disabled = false;
      if (row) row.classList.remove('cart-gavepose--busy');
      console.error('[byRavn-gavepose]', error);
      // Re-render from the server: the DOM's line keys may be stale (Zepto re-keys
      // lines after add), and a fresh render heals keys and checkbox state so the
      // toggle can simply be retried.
      cartSurfaces().forEach((s) => s.onCartUpdate());
    } finally {
      setBusy(false);
    }
  }

  document.addEventListener(
    'change',
    (event) => {
      const checkbox = event.target && event.target.closest && event.target.closest('.cart-gavepose__checkbox');
      if (!checkbox) return;
      event.stopPropagation();
      onToggle(checkbox);
    },
    true
  );

  // Gift-bag info popup, opened by the per-line info button.
  let modalTrigger = null;

  function drawerOpen() {
    const drawer = document.querySelector('cart-drawer');
    return !!drawer && drawer.classList.contains('active');
  }

  function openInfoModal(trigger) {
    const modal = document.getElementById('GaveposeInfoModal');
    if (!modal) return;
    modalTrigger = trigger || null;
    modal.hidden = false;
    // Force a reflow so the opacity transition plays; rAF is throttled in
    // background/automation tabs and would delay the fade-in.
    void modal.offsetWidth;
    modal.classList.add('cart-gavepose-modal--active');
    document.body.classList.add('overflow-hidden');
    const closeButton = modal.querySelector('[data-gavepose-close]');
    if (closeButton) closeButton.focus();
  }

  function closeInfoModal() {
    const modal = document.getElementById('GaveposeInfoModal');
    if (!modal || modal.hidden) return;
    modal.classList.remove('cart-gavepose-modal--active');
    if (!drawerOpen()) document.body.classList.remove('overflow-hidden');
    setTimeout(() => {
      if (!modal.classList.contains('cart-gavepose-modal--active')) modal.hidden = true;
    }, 250);
    if (modalTrigger) {
      modalTrigger.focus();
      modalTrigger = null;
    }
  }

  document.addEventListener('click', (event) => {
    if (!event.target.closest) return;
    const opener = event.target.closest('[data-gavepose-info]');
    if (opener) {
      event.preventDefault();
      openInfoModal(opener);
      return;
    }
    if (event.target.closest('[data-gavepose-close]')) {
      event.preventDefault();
      closeInfoModal();
    }
  });

  let swallowEscapeKeyup = false;

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const modal = document.getElementById('GaveposeInfoModal');
    if (!modal || modal.hidden || !modal.classList.contains('cart-gavepose-modal--active')) {
      // Modal not open: clear any stale flag so a plain Escape still closes the drawer.
      swallowEscapeKeyup = false;
      return;
    }
    // Dismiss only the modal. <cart-drawer> closes on keyup, and closeInfoModal
    // returns focus to the info button inside the drawer, so the trailing keyup
    // would otherwise bubble there and close the drawer too — swallow that one keyup.
    event.stopPropagation();
    swallowEscapeKeyup = true;
    closeInfoModal();
  });

  document.addEventListener(
    'keyup',
    (event) => {
      if (swallowEscapeKeyup && event.key === 'Escape') {
        event.stopPropagation();
        swallowEscapeKeyup = false;
      }
    },
    true
  );
})();
